/**
 * Cart Context
 *
 * Provides cart state management with localStorage persistence.
 * Supports adding/removing OBJKT listings and calculating totals.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type { CartItem, CartContextValue, CartState } from '@/lib/types/cart';
import { NETWORK } from '@/lib/config';

const CART_STORAGE_KEY = 'tezos-marketplace-cart';
const CART_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

const CartContext = createContext<CartContextValue | null>(null);

interface StoredCartData {
  items: CartItem[];
  network: string;
  updatedAt: number;
}

function loadCartFromStorage(): CartItem[] {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (!stored) return [];

    const data: StoredCartData = JSON.parse(stored);
    const now = Date.now();

    // Clear cart if network changed (ghostnet items don't work on mainnet and vice versa)
    if (data.network && data.network !== NETWORK) {
      console.log(`[Cart] Network changed from ${data.network} to ${NETWORK}, clearing cart`);
      localStorage.removeItem(CART_STORAGE_KEY);
      return [];
    }

    // Filter out expired items (older than 24 hours)
    const validItems = (data.items || []).filter(
      (item: CartItem) => now - item.addedAt < CART_MAX_AGE_MS
    );

    return validItems;
  } catch (error) {
    console.error('[Cart] Failed to load from storage:', error);
    return [];
  }
}

function saveCartToStorage(items: CartItem[]): void {
  try {
    localStorage.setItem(
      CART_STORAGE_KEY,
      JSON.stringify({ items, network: NETWORK, updatedAt: Date.now() })
    );
  } catch (error) {
    console.error('[Cart] Failed to save to storage:', error);
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CartState>({
    items: [],
    isOpen: false,
  });

  // Load cart from localStorage on mount
  useEffect(() => {
    const items = loadCartFromStorage();
    if (items.length > 0) {
      setState((prev) => ({ ...prev, items }));
    }
  }, []);

  // Save cart to localStorage when items change
  useEffect(() => {
    saveCartToStorage(state.items);
  }, [state.items]);

  const addItem = useCallback((item: Omit<CartItem, 'addedAt'>) => {
    setState((prev) => {
      // Check if already in cart
      if (prev.items.some((i) => i.listingId === item.listingId)) {
        return prev;
      }

      const newItem: CartItem = {
        ...item,
        addedAt: Date.now(),
      };

      return {
        ...prev,
        items: [...prev.items, newItem],
      };
    });
  }, []);

  const removeItem = useCallback((listingId: string) => {
    setState((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.listingId !== listingId),
    }));
  }, []);

  const clearCart = useCallback(() => {
    setState((prev) => ({
      ...prev,
      items: [],
    }));
  }, []);

  const isInCart = useCallback(
    (listingId: string) => {
      return state.items.some((item) => item.listingId === listingId);
    },
    [state.items]
  );

  const openCart = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: true }));
  }, []);

  const closeCart = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const toggleCart = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  const totalPriceMutez = useMemo(() => {
    return state.items
      .reduce((sum, item) => sum + BigInt(item.priceMutez), BigInt(0))
      .toString();
  }, [state.items]);

  const totalPriceXtz = useMemo(() => {
    const mutez = BigInt(totalPriceMutez);
    const xtz = Number(mutez) / 1_000_000;
    return xtz.toFixed(2);
  }, [totalPriceMutez]);

  const value: CartContextValue = {
    items: state.items,
    isOpen: state.isOpen,
    addItem,
    removeItem,
    clearCart,
    isInCart,
    openCart,
    closeCart,
    toggleCart,
    totalPriceMutez,
    totalPriceXtz,
    itemCount: state.items.length,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
